import { Eye, EyeOff } from 'lucide-react'
import { Button, EnrollmentHelpText } from '@/shared/components'
import { StepHeader } from '../StepHeader'
import { useContactDetailsStep } from './contactDetails/useContactDetailsStep'
import type { EligibilityFormData } from './types'
import type { EnrollmentFieldEditability } from '../../utils/enrollmentHydration'

type ContactDetailsStepProps = {
  currentStep: number
  totalSteps: number
  fieldEditability: EnrollmentFieldEditability
  formData: EligibilityFormData
  updateFormData: (data: Partial<EligibilityFormData>) => void
  onBack: () => void
  onContinue: () => void
  isSubmitting?: boolean
  errorMessage?: string | null
}

export function ContactDetailsStep({
  currentStep,
  totalSteps,
  fieldEditability,
  formData,
  updateFormData,
  onBack,
  onContinue,
  isSubmitting = false,
  errorMessage = null
}: ContactDetailsStepProps) {
  const isEmergencyContactNameDisabled = fieldEditability.emergencyContactName === false
  const isEmergencyContactPhoneDisabled = fieldEditability.emergencyContactPhone === false

  const {
    emergencyContactMatchesApplicantPhone,
    emergencyContactPhoneDigits,
    handleBackClick,
    handleEmergencyContactNameChange,
    handleEmergencyContactPhoneChange,
    handleSsnChange,
    hasValidSsnValue,
    maskedSsnInputStyle,
    setShowSsn,
    ssnLengthError,
    showSsn
  } = useContactDetailsStep({
    formData,
    onBack,
    updateFormData
  })

  return (
    <div className="bg-white px-4 py-2">
      <div className="mx-auto max-w-4xl animate-fade-in-up">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={handleBackClick} />

        <div className="mb-5 flex items-start justify-center animate-fade-in-up">
          <h1 className="modal-heading__title text-center">Contact Details</h1>
        </div>

        <div className="mx-auto max-w-md space-y-4 pt-6">
          <div className="space-y-4 pb-8">
            {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}

            <div className="animate-fade-in-up" style={{ animationDelay: '120ms' }}>
              <label className="enrollment-field-label">
                Social Security Number (SSN) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  name="government-id"
                  spellCheck={false}
                  placeholder="XXX-XX-XXXX"
                  value={formData.ssn}
                  onChange={e => handleSsnChange(e.target.value)}
                  required
                  className="enrollment-field-control enrollment-field-control--icon-right"
                  style={maskedSsnInputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowSsn(prev => !prev)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 border-0 bg-transparent p-0 text-[#8a7d70] shadow-none outline-none transition-colors duration-200 hover:text-primary min-w-[30px] flex items-center justify-center"
                  aria-label={showSsn ? 'Hide SSN' : 'Show SSN'}
                >
                  {showSsn ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {ssnLengthError ? <p className="mt-1 text-xs text-red-600">{ssnLengthError}</p> : null}
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <label className="enrollment-field-label">Emergency Contact Name</label>
              <input
                type="text"
                placeholder="Full Name"
                value={formData.emergencyContactName}
                disabled={isEmergencyContactNameDisabled}
                onChange={e => handleEmergencyContactNameChange(e.target.value)}
                className="enrollment-field-control"
              />
            </div>

            <div className="animate-fade-in-up" style={{ animationDelay: '280ms' }}>
              <label className="enrollment-field-label">
                Emergency Contact Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="(XXX) XXX-XXXX"
                value={formData.emergencyContactPhone}
                disabled={isEmergencyContactPhoneDisabled}
                onChange={e => handleEmergencyContactPhoneChange(e.target.value)}
                className="enrollment-field-control"
              />
              {emergencyContactMatchesApplicantPhone ? (
                <p className="mt-1 text-xs text-amber-700">
                  Emergency contact phone must be different from your phone number.
                </p>
              ) : null}
            </div>

            <div className="animate-fade-in-up pt-1" style={{ animationDelay: '340ms' }}>
              <Button
                type="button"
                onClick={onContinue}
                disabled={
                  isSubmitting ||
                  emergencyContactMatchesApplicantPhone ||
                  !hasValidSsnValue ||
                  emergencyContactPhoneDigits.length !== 10
                }
                fullWidth
                className="hover:-translate-y-0.5 active:scale-95"
              >
                {isSubmitting ? 'Saving...' : 'Continue'}
              </Button>
            </div>

            {/* <div className="animate-fade-in-up" style={{ animationDelay: '400ms' }}>
            <Button
              type="button"
              variant="outline"
              fullWidth
              className="hover:-translate-y-0.5 active:scale-95"
            >
              Save Your Progress
            </Button>
          </div> */}

            <EnrollmentHelpText />
          </div>
        </div>
      </div>
    </div>
  )
}
